import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { useAuth } from '../../hooks/use-auth';
import * as testsService from '../../services/api/tests';
import { Test, TestStatus } from '../../entities/test';
import TestsTableComponent from '../../components/tests/table';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import TestsFilter from '../../components/tests/filter';
import AlertPrimary from '../../components/common/alerts/alert-primary';

interface TestsFilterProps {
  status?: string;
  administratorId?: number | string;
  productId?: number;
  page?: number;
  textSearch?: string;
  siteId?: number;
  questionnaireFilled?: string;
  addons?: string;
}

const cookies = new Cookies();
const FILTER_COOKIE_NAME = 'admin_tests_filter_preferences';

function TestsListPageComponent() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const page = query.get('page');
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();

  const getSavedFilter = (): TestsFilterProps => {
    const savedPreferences = cookies.get(FILTER_COOKIE_NAME);

    if (savedPreferences && savedPreferences.rememberFilter) {
      return savedPreferences.filterData || { status: TestStatus.BloodworkDone };
    }

    return { status: TestStatus.BloodworkDone };
  };

  const getInitialState = (): TestsFilterProps => {
    const urlParams: TestsFilterProps = {};

    searchParams.forEach((value, key) => {
      if (key === 'page' || key === 'productId' || key === 'siteId') {
        urlParams[key as keyof TestsFilterProps] = parseInt(value, 10) as never;
      } else if (key === 'administratorId') {
        if (value === 'unassigned' || value === 'me' || value === 'unassigned-or-mine') {
          urlParams.administratorId = value;
        } else if (!Number.isNaN(Number(value))) {
          urlParams.administratorId = parseInt(value, 10);
        } else {
          urlParams.administratorId = value;
        }
      } else {
        urlParams[key as keyof TestsFilterProps] = value as never;
      }
    });

    if (Object.keys(urlParams).length > 0) {
      return urlParams;
    }

    return getSavedFilter();
  };

  const [params, setParams] = useState<TestsFilterProps>(getInitialState());
  const [pages, setPages] = useState<number>(1);
  const [currPage, setCurrPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();

  const [tests, setTests] = useState<Test[]>([]);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const limit = 50;
        const offset = (currPage - 1) * limit;

        let administratorIdForApi = params.administratorId;

        if (administratorIdForApi === 'me') {
          administratorIdForApi = user?.id;
        } else if (administratorIdForApi === 'unassigned-or-mine') {
          administratorIdForApi = `unassigned-or-mine:${user?.id}`;
        }

        const response = await testsService.list(token, {
          ...params,
          administratorId: administratorIdForApi,
          limit,
          offset,
        });

        setTests(response.tests);
        setTotal(response.total);
        setPages(Math.ceil(response.total / limit));
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token, params, currPage, user?.id]);

  const setFilterData = (filterData: TestsFilterProps, rememberFilter?: boolean) => {
    setParams({ ...filterData, page: 1 });
    setCurrPage(1);

    if (rememberFilter !== undefined) {
      if (rememberFilter) {
        cookies.set(
          FILTER_COOKIE_NAME,
          { rememberFilter: true, filterData },
          {
            maxAge: 30 * 24 * 60 * 60,
            path: '/',
          },
        );
      } else {
        cookies.remove(FILTER_COOKIE_NAME, { path: '/' });
      }
    } else {
      const savedPreferences = cookies.get(FILTER_COOKIE_NAME);

      if (savedPreferences && savedPreferences.rememberFilter) {
        cookies.set(
          FILTER_COOKIE_NAME,
          { rememberFilter: true, filterData },
          {
            maxAge: 30 * 24 * 60 * 60,
            path: '/',
          },
        );
      }
    }

    // Filter out undefined/null values before setting search params
    const cleanParams = Object.entries(filterData).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== undefined && value !== null && !Number.isNaN(value)) {
        acc[key] = String(value);
      }

      return acc;
    }, {});

    setSearchParams(cleanParams);
  };

  const setPageNo = (currentPage: number) => {
    setCurrPage(currentPage);
    setParams({ ...params, page: currentPage });

    // Filter out undefined/null values before setting search params
    const cleanParams = Object.entries({ ...params, page: currentPage.toString() }).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null && !Number.isNaN(value)) {
          acc[key] = String(value);
        }

        return acc;
      },
      {},
    );

    setSearchParams(cleanParams);
  };

  useEffect(() => {
    if (page) {
      setCurrPage(Number(page));
    }
  }, [page]);

  return (
    <div>
      <header className="mb-3 mt-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">Tests</h1>
          <p className="mt-1 text-sm text-gray-600">Total: {total} tests</p>
        </div>
      </header>
      <main>
        <div className="w-full px-3">
          {error && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-5">
              <AlertPrimary text={error} />
            </div>
          )}
          <TestsFilter onUpdate={setFilterData} filterState={params} />
          {isLoading ? (
            <div className="flex justify-center py-12">
              <CommonAnimatedSpinner />
            </div>
          ) : (
            <>
              {tests.length > 0 && (
                <TestsTableComponent tests={tests} pages={pages} onUpdate={setPageNo} currPageNo={currPage} />
              )}
              {tests.length === 0 && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">No tests found</div>}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default TestsListPageComponent;
